import SectionHeading from "@/components/base/SectionHeading";
import Reveal from "@/components/base/Reveal";
import { boardItems, quickItems, heatThreads } from "@/mocks/home";

const rankTone: Record<number, string> = {
  1: "bg-primary-500 text-background-50",
  2: "bg-primary-400 text-background-50",
  3: "bg-secondary-500 text-foreground-950",
};

export default function HotBoard() {
  return (
    <section id="board" className="w-full border-b border-background-200 bg-background-50">
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8 md:py-20">
        <Reveal>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <SectionHeading
              eyebrow="今日热瓜榜"
              tone="primary"
              title="社区此刻正在追的 6 条新料"
              description="按讨论热度排序，每条都标注了分区、来源时间与社区回帖量。标题先行、摘要补充，三十秒看完今天最值得跟的内容。"
            />
            <a
              href="#stream"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              查看完整速看流
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <ol className="flex flex-col gap-3">
              {boardItems.map((item, index) => (
                <Reveal key={item.rank} delay={index * 60}>
                  <li className="group relative overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
                    <span className="absolute inset-y-0 left-0 w-1 bg-primary-500/0 transition-colors group-hover:bg-primary-500" />
                    <div className="flex gap-4 p-4 md:p-5">
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-base font-bold md:h-10 md:w-10 md:text-lg ${
                          rankTone[item.rank] ?? "bg-background-200 text-foreground-700"
                        }`}
                      >
                        {item.rank}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900">
                            {item.tag}
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs text-primary-700">
                            <i className="ri-fire-line" />
                            {item.hot}
                          </span>
                        </div>
                        <h3 className="mt-2 text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                          {item.title}
                        </h3>
                        <p className="clamp-2 mt-2 text-sm leading-relaxed text-foreground-600">
                          {item.summary}
                        </p>
                        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                          <span className="inline-flex items-center gap-1">
                            <i className="ri-time-line" />
                            {item.time}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <i className="ri-chat-3-line" />
                            {item.comments} 条回帖
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <i className="ri-shield-check-line" />
                            已标注来源
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>

          <div className="flex flex-col gap-6">
            <Reveal delay={120}>
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <div className="flex items-center justify-between">
                  <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className="ri-pulse-line text-primary-600" />
                    即时速递
                  </h3>
                  <span className="inline-flex items-center gap-1 text-xs text-accent-700">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-accent-500 animate-softPulse" />
                    实时
                  </span>
                </div>
                <ul className="mt-4 flex flex-col divide-y divide-background-200">
                  {quickItems.map((item) => (
                    <li key={item.text} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                      <span className="mt-1.5 inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-primary-400" />
                      <div>
                        <p className="text-sm leading-relaxed text-foreground-800">{item.text}</p>
                        <span className="mt-1 block text-xs text-foreground-500">{item.time}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={180}>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-discuss-line text-accent-600" />
                  讨论热度榜
                </h3>
                <ul className="mt-4 flex flex-col gap-3">
                  {heatThreads.map((thread, index) => (
                    <li key={thread.title} className="flex items-start gap-3">
                      <span className="mt-0.5 font-heading text-sm font-bold text-background-400">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <a
                        href="#talk"
                        className="flex-1 text-sm leading-snug text-foreground-700 transition-colors hover:text-primary-700"
                      >
                        {thread.title}
                      </a>
                      <span className="shrink-0 rounded-full bg-accent-100 px-2 py-0.5 text-xs text-accent-800">
                        {thread.replies}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={240}>
              <div className="rounded-lg border border-primary-200 bg-primary-100/70 p-5">
                <h3 className="font-heading text-lg font-bold text-primary-900">吃瓜前先看这条</h3>
                <p className="mt-2 text-sm leading-relaxed text-primary-800/90">
                  页面上的内容分三类：<strong className="font-semibold">官方回应</strong>、
                  <strong className="font-semibold">多源提及</strong> 与
                  <strong className="font-semibold">待核实线索</strong>。跟话题之前，先看清它属于哪一类。
                </p>
                <a
                  href="#trust"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
                >
                  了解核实标准
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}